import React from 'react';
import { DeviceTypeChip, Mono } from './RmaBits';

// Screen 1, lower half — the on-ramp into an RMA: units that failed goods-inwards QC,
// grouped by PO batch. QC no longer creates a shipment at Finish; failures land here so a
// supplier claim can be raised per batch and stay traceable to that delivery.
//
// Field-quarantined units are not listed here — they are raised by scanning the serial on
// the New RMA screen, which reads the quarantine fault off the record anyway.
//
// Nothing in this file writes to state. Every action hands device ids up to RmaModule, which
// navigates to the New RMA screen with them pre-filled.

const QC_COLS = '118px 106px 190px minmax(0,1fr) 112px';

function SectionCard({ title, sub, action, onAction, children }) {
  return (
    <div style={{ background: 'var(--surface-default)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-xl)', overflow: 'hidden' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '14px 18px', borderBottom: '1px solid var(--border-default)' }}>
        <div>
          <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{title}</div>
          {sub && <div style={{ fontSize: 11.5, color: 'var(--text-subtle)', marginTop: 3 }}>{sub}</div>}
        </div>
        {action && onAction && (
          <button onClick={onAction} className="btn btn-sm btn-brand-ghost" style={{ flexShrink: 0 }}>{action}</button>
        )}
      </div>
      {children}
    </div>
  );
}

function RmaQueueStaging({ qcStaged, isReadOnly, onAddDevices }) {
  const totalQc = qcStaged.reduce((n, b) => n + b.devices.length, 0);

  if (!qcStaged.length) return null;

  return (
    <>
      {qcStaged.length > 0 && (
        <SectionCard
          title="Failed goods-inwards QC — staged for RMA"
          sub={totalQc + (totalQc === 1 ? ' unit' : ' units') + ' failed QC on arrival and were never deployed. Group them by batch so the supplier claim stays traceable.'}
          action={!isReadOnly && qcStaged.length > 1 ? 'Raise one RMA per batch' : undefined}
          onAction={!isReadOnly && qcStaged.length > 1
            ? () => onAddDevices(qcStaged.flatMap(b => b.devices.map(d => d.id)), { perBatch: qcStaged })
            : undefined}
        >
          {qcStaged.map(batch => (
            <div key={batch.batchNo}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '11px 18px', background: 'var(--surface-inset)', borderBottom: '1px solid var(--border-subtle)', flexWrap: 'wrap' }}>
                <Mono>{batch.batchNo}</Mono>
                {batch.types.map(t => <DeviceTypeChip key={t} type={t} />)}
                <span style={{ fontSize: 11.5, color: 'var(--text-subtle)' }}>{batch.meta}</span>
                <span style={{ flex: 1 }} />
                {!isReadOnly && (
                  <button
                    onClick={() => onAddDevices(batch.devices.map(d => d.id), { batchNo: batch.batchNo })}
                    className="btn btn-sm btn-ghost"
                  >Add batch to RMA</button>
                )}
              </div>
              {batch.devices.map(d => (
                <div
                  key={d.id}
                  style={{ display: 'grid', gridTemplateColumns: QC_COLS, gap: 14, padding: '11px 18px', borderBottom: '1px solid var(--border-subtle)', alignItems: 'center' }}
                  onMouseEnter={e => e.currentTarget.style.background = 'var(--surface-raised)'}
                  onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                >
                  <Mono>{d.serial}</Mono>
                  <Mono size={11.5} weight={400} color="var(--text-subtle)">{d.deviceId || '—'}</Mono>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 5 }}>
                    {(d.quarantineFailedChecks || []).length
                      ? d.quarantineFailedChecks.map(c => (
                          <span key={c} className="pill pill-danger" style={{ padding: '2px 8px', fontSize: 11 }}>{c}</span>
                        ))
                      : <span style={{ fontSize: 11, color: 'var(--text-ghost)' }}>—</span>}
                  </div>
                  <div style={{ fontSize: 12.5, color: 'var(--text-muted)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {d.quarantineNote || '—'}
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                    {!isReadOnly && (
                      <button onClick={() => onAddDevices([d.id], { batchNo: batch.batchNo })} className="btn btn-sm btn-ghost">
                        Add to RMA
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ))}
        </SectionCard>
      )}

    </>
  );
}

export default RmaQueueStaging;
